import React, { useState, useEffect } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  ScrollView, 
  TouchableOpacity, 
  ActivityIndicator 
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  addDoc, 
  Timestamp 
} from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { typography, spacing, borderRadius } from '../../constants/theme';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { Exercise } from '../../types';
import { useTheme } from '../../context/ThemeContext';
import { MUSCLE_GROUPS } from '../../constants/muscleGroups';
import { Ionicons } from '@expo/vector-icons';

type Props = NativeStackScreenProps<MainStackParamList, 'ExerciseDetail'>;

interface ExerciseProgress {
  id: string;
  weight: number;
  series: number;
  reps: number;
  date: Timestamp;
  notes?: string;
}

interface RoutineExercise {
  exerciseId: string;
  exerciseName: string;
  series: number;
  repetitions: number;
  restTime: number;
  muscleGroups?: string[];
}

const ExerciseDetailScreen = ({ route, navigation }: Props) => {
  const { exerciseId, routineId, dayOrder } = route.params;
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [loading, setLoading] = useState(true);
  const [progressHistory, setProgressHistory] = useState<ExerciseProgress[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const [weight, setWeight] = useState('');
  const [series, setSeries] = useState('');
  const [reps, setReps] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    fetchExerciseDetails();
    fetchProgressHistory();
    fetchDefaultValues();
  }, [exerciseId]);

  const fetchExerciseDetails = async () => {
    try {
      const exerciseRef = doc(firestore, 'exercises', exerciseId);
      const exerciseDoc = await getDoc(exerciseRef);
      
      if (!exerciseDoc.exists()) {
        setErrorMessage('No se encontró el ejercicio');
        navigation.goBack();
        return;
      }
      
      const exerciseData = {
        id: exerciseDoc.id,
        ...exerciseDoc.data()
      } as Exercise;
      
      setExercise(exerciseData);
    } catch (error) {
      console.error('Error al obtener detalles del ejercicio:', error);
      setErrorMessage('No se pudieron cargar los detalles del ejercicio');
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultValues = async () => {
    try {
      if (!routineId || !dayOrder) return;

      const routineRef = doc(firestore, 'routines', routineId);
      const routineDoc = await getDoc(routineRef);
      
      if (!routineDoc.exists()) return;
      
      const routineData = routineDoc.data();
      const days = routineData.days || [];
      
      const currentDay = days.find((day: any) => day.order === dayOrder);
      if (!currentDay) return;
      
      const exerciseConfig = currentDay.exercises.find(
        (ex: RoutineExercise) => ex.exerciseId === exerciseId
      );
      
      if (exerciseConfig) {
        setSeries(exerciseConfig.series.toString());
        setReps(exerciseConfig.repetitions.toString());
      }
    } catch (error) {
      console.error('Error al obtener valores por defecto:', error);
    }
  };

  const fetchProgressHistory = async () => {
    try {
      setHistoryLoading(true);
      setHistoryError('');
      
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      const progressRef = collection(firestore, 'progress');
      const q = query(
        progressRef,
        where('userId', '==', userId),
        where('exerciseId', '==', exerciseId),
        where('routineId', '==', routineId),
        orderBy('date', 'desc'),
        limit(10)
      );
      
      const querySnapshot = await getDocs(q);
      const history: ExerciseProgress[] = [];
      
      querySnapshot.forEach((doc) => {
        history.push({
          id: doc.id,
          ...doc.data()
        } as ExerciseProgress);
      });
      
      setProgressHistory(history);
      
      // Pre-fill with last weight if available
      if (history.length > 0) {
        setWeight(history[0].weight.toString());
      }
    } catch (error) {
      console.error('Error al obtener historial de progreso:', error);
      setHistoryError('No se pudo cargar el historial de progreso');
    } finally {
      setHistoryLoading(false);
    }
  };

  const saveProgress = async () => {
    try {
      setSaving(true);
      setErrorMessage('');
      
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      if (!weight.trim() || !series.trim() || !reps.trim()) {
        setErrorMessage('Por favor completa los campos de peso, series y repeticiones');
        return;
      }
      
      const progressData = {
        userId,
        exerciseId,
        routineId,
        dayOrder,
        weight: parseFloat(weight),
        series: parseInt(series),
        reps: parseInt(reps),
        date: Timestamp.now(),
        notes: notes.trim() || null
      };
      
      await addDoc(collection(firestore, 'progress'), progressData);
      
      // Navegar hacia atrás después de guardar
      navigation.goBack();
      
    } catch (error) {
      console.error('Error al guardar progreso:', error);
      setErrorMessage('No se pudo guardar el progreso');
    } finally {
      setSaving(false);
    }
  };

  const renderProgressItem = (item: ExerciseProgress, index: number) => {
    const date = item.date.toDate();
    const formattedDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    
    return (
      <Card key={item.id || index} style={styles.progressCard}>
        <View style={styles.progressHeader}>
          <Text style={[styles.progressDate, { color: theme.colors.primary }]}>{formattedDate}</Text>
        </View>
        
        <View style={styles.progressDetails}>
          <View style={styles.progressItem}>
            <Text style={[styles.progressLabel, { color: theme.colors.grey.dark }]}>Peso</Text>
            <Text style={[styles.progressValue, { color: theme.colors.text }]}>{item.weight} kg</Text>
          </View>
          
          <View style={styles.progressItem}>
            <Text style={[styles.progressLabel, { color: theme.colors.grey.dark }]}>Series</Text>
            <Text style={[styles.progressValue, { color: theme.colors.text }]}>{item.series}</Text>
          </View>
          
          <View style={styles.progressItem}>
            <Text style={[styles.progressLabel, { color: theme.colors.grey.dark }]}>Repeticiones</Text>
            <Text style={[styles.progressValue, { color: theme.colors.text }]}>{item.reps}</Text>
          </View>
        </View>
        
        {item.notes && (
          <View style={[styles.notesContainer, { borderTopColor: theme.colors.grey.light }]}>
            <Text style={[styles.notesLabel, { color: theme.colors.grey.dark }]}>Notas:</Text>
            <Text style={[styles.notesText, { color: theme.colors.text }]}>{item.notes}</Text>
          </View>
        )}
      </Card>
    );
  };

  // Función para obtener el nombre del grupo muscular a partir del ID
  const getMuscleGroupName = (muscleGroupId: string): string => {
    const muscleGroup = MUSCLE_GROUPS.find(group => group.id === muscleGroupId);
    return muscleGroup ? muscleGroup.name : muscleGroupId;
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (!exercise) {
    return (
      <View style={[styles.errorContainer, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.errorText, { color: theme.colors.error }]}>No se pudo cargar el ejercicio</Text>
        <Button 
          title="Volver" 
          onPress={() => navigation.goBack()} 
          type="primary" 
        />
      </View>
    );
  }

  return (
    <ScrollView style={[styles.scrollView, { backgroundColor: theme.colors.background }]}>
      <View style={styles.container}>
        {/* Exercise Header */}
        <View style={styles.exerciseHeader}>
          <Text style={[styles.exerciseTitle, { color: theme.colors.text }]}>{exercise.name}</Text>
          
          <View style={styles.muscleGroups}>
            {exercise.muscleGroups.map((muscleId, index) => (
              <View key={index} style={[styles.muscleTag, { backgroundColor: theme.colors.primary }]}>
                <Text style={[styles.muscleText, { color: theme.colors.white }]}>
                  {getMuscleGroupName(muscleId)}
                </Text>
              </View>
            ))}
          </View>
          
          <Text style={[styles.difficultyText, { color: theme.colors.grey.dark }]}>
            Nivel: {
              exercise.difficultyLevel === 'beginner' ? 'Principiante' :
              exercise.difficultyLevel === 'intermediate' ? 'Intermedio' : 'Avanzado'
            }
          </Text>
        </View>
        
        {/* Detalles colapsables */}
        <TouchableOpacity 
          style={[styles.collapsibleHeader, { backgroundColor: theme.colors.primary + '20' }]}
          onPress={() => setShowDetails(!showDetails)}
        >
          <Text style={[styles.collapsibleTitle, { color: theme.colors.primary }]}>
            Detalles del Ejercicio
          </Text>
          <Ionicons 
            name={showDetails ? 'chevron-up' : 'chevron-down'} 
            size={24} 
            color={theme.colors.primary} 
          />
        </TouchableOpacity>
        
        {showDetails && (
          <View style={styles.detailsContainer}>
            {/* Exercise Description */}
            <Card style={styles.card}>
              <Text style={[styles.cardTitle, { color: theme.colors.text }]}>Descripción</Text>
              <Text style={[styles.cardText, { color: theme.colors.text }]}>{exercise.description}</Text>
            </Card>
            
            {/* Exercise Instructions */}
            <Card style={styles.card}>
              <Text style={[styles.cardTitle, { color: theme.colors.text }]}>Instrucciones</Text>
              <Text style={[styles.cardText, { color: theme.colors.text }]}>{exercise.instructions}</Text>
            </Card>
          </View>
        )}
        
        {/* Record Progress - Parte principal */}
        <Card style={styles.card}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>Registrar Progreso</Text>
          
          <View style={styles.inputRow}>
            <View style={styles.inputHalf}>
              <Input
                label="Peso (kg)"
                value={weight}
                onChangeText={setWeight}
                keyboardType="numeric"
                placeholder="0"
              />
            </View>
            
            <View style={styles.inputHalf}>
              <Input
                label="Series"
                value={series}
                onChangeText={setSeries}
                keyboardType="numeric"
                placeholder="0"
              />
            </View>
          </View>
          
          <Input
            label="Repeticiones"
            value={reps}
            onChangeText={setReps}
            keyboardType="numeric"
            placeholder="0"
          />
          
          <Input
            label="Notas (opcional)"
            value={notes}
            onChangeText={setNotes}
            multiline
            numberOfLines={3}
            placeholder="Escribe tus notas aquí..."
          />
          
          {errorMessage ? (
            <Text style={[styles.errorMessage, { color: theme.colors.error }]}>
              {errorMessage}
            </Text>
          ) : null}
          
          <Button
            title="Guardar Progreso"
            onPress={saveProgress}
            type="primary"
            loading={saving}
            style={styles.saveButton}
          />
        </Card>
        
        {/* Progress History - Colapsable */}
        <TouchableOpacity 
          style={[styles.collapsibleHeader, { backgroundColor: theme.colors.primary + '20' }]}
          onPress={() => setShowHistory(!showHistory)}
        >
          <Text style={[styles.collapsibleTitle, { color: theme.colors.primary }]}>
            Historial de Progreso
          </Text>
          <Ionicons 
            name={showHistory ? 'chevron-up' : 'chevron-down'} 
            size={24} 
            color={theme.colors.primary} 
          />
        </TouchableOpacity>
        
        {showHistory && (
          <Card style={styles.card}>
            {historyLoading ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : historyError ? (
              <Text style={[styles.errorMessage, { color: theme.colors.error }]}>
                {historyError}
              </Text>
            ) : progressHistory.length > 0 ? (
              <View style={styles.progressHistory}>
                {progressHistory.map((item, index) => renderProgressItem(item, index))}
              </View>
            ) : (
              <Text style={[styles.emptyText, { color: theme.colors.grey.dark }]}>No hay registros de progreso</Text>
            )}
          </Card>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  container: {
    padding: spacing.lg,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  errorText: {
    fontSize: typography.fontSize.lg,
    marginBottom: spacing.lg,
    textAlign: 'center',
  },
  exerciseHeader: {
    marginBottom: spacing.lg,
  },
  exerciseTitle: {
    fontSize: typography.fontSize.xxl,
    fontWeight: 'bold',
    marginBottom: spacing.sm,
  },
  muscleGroups: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: spacing.sm,
  },
  muscleTag: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.sm,
    marginRight: spacing.xs,
    marginBottom: spacing.xs,
  },
  muscleText: {
    fontSize: typography.fontSize.sm,
  },
  difficultyText: {
    fontSize: typography.fontSize.md,
  },
  card: {
    marginBottom: spacing.lg,
  },
  cardTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginBottom: spacing.md,
  },
  cardText: {
    fontSize: typography.fontSize.md,
    lineHeight: 24,
  },
  inputRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  inputHalf: {
    width: '48%',
  },
  saveButton: {
    marginTop: spacing.md,
  },
  progressHistory: {
    marginTop: spacing.md,
  },
  progressCard: {
    marginBottom: spacing.md,
    padding: spacing.md,
  },
  progressHeader: {
    marginBottom: spacing.sm,
  },
  progressDate: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
  },
  progressDetails: {
    marginBottom: spacing.sm,
  },
  progressItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  progressLabel: {
    fontSize: typography.fontSize.md,
  },
  progressValue: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  notesContainer: {
    borderTopWidth: 1,
    paddingTop: spacing.sm,
  },
  notesLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
    marginBottom: spacing.xs,
  },
  notesText: {
    fontSize: typography.fontSize.sm,
  },
  emptyText: {
    fontSize: typography.fontSize.md,
    textAlign: 'center',
    marginVertical: spacing.md,
  },
  collapsibleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
  },
  collapsibleTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  detailsContainer: {
    marginBottom: spacing.md,
  },
  errorMessage: {
    fontSize: typography.fontSize.md,
    textAlign: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.xs,
  },
});

export default ExerciseDetailScreen; 